import { Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TFunction } from 'react-i18next';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Typography from '../../../componentsv2/Typography';
import Section from '../../../componentsv2/Section';
import NumericText from '../../../componentsv2/NumericText';
import Button from '../../../componentsv2/Button';
import { BANKING_ACCOUNT_DETAIL, BANKING_GLOBAL_ACCOUNTS, path } from '../../../constants/auth';
import { COLORS } from '../../../themev2';

const currencySymbols = {
  USD: '$',
  MXN: '$',
  EUR: '€',
};

const PREFERRED_ORDER = ['USD', 'MXN', 'EUR'];

interface RawAccount {
  currency: string;
  availableAmount: number | string;
}

interface AccountBalancesTableProps {
  accountBalances: RawAccount[];
  selectedCurrencies: string[];
  isLoading: boolean;
  compact?: boolean;
  t: TFunction;
}

export default function AccountBalancesTable({
  accountBalances,
  selectedCurrencies,
  isLoading,
  compact = false,
  t,
}: AccountBalancesTableProps) {
  const navigate = useNavigate();
  const accounts = useMemo(() => {
    const selected = accountBalances
      .filter((a) => selectedCurrencies.includes(a.currency))
      .map(({ currency: name, availableAmount: balance }) => ({ name, balance: Number(balance) }));

    return selected.sort((a, b) => {
      const aIdx = PREFERRED_ORDER.indexOf(a.name);
      const bIdx = PREFERRED_ORDER.indexOf(b.name);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    });
  }, [accountBalances, selectedCurrencies]);

  const rowPaddingY = compact ? '0.75em' : '1.5em';
  const showInlineViewAll = compact && accountBalances.length > 3;
  const displayedAccounts = showInlineViewAll ? accounts.slice(0, 2) : accounts;

  return (
    <Section
      sx={{
        borderTopLeftRadius: compact ? undefined : 0,
        borderTopRightRadius: compact ? undefined : 0,
        padding: '0 1em',
        paddingBottom: compact ? 0 : '1em',
        flex: 1,
        ...(compact && { display: 'flex', flexDirection: 'column' }),
      }}
    >
      {isLoading ? (
        <Box
          sx={
            compact
              ? { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }
              : {}
          }
        >
          {[1, 2, 3].map((_, index, arr) => (
            <Fragment key={index}>
              <Box
                sx={{
                  paddingY: compact ? 0 : rowPaddingY,
                  flex: compact ? 1 : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <Skeleton
                  variant="circular"
                  width={compact ? 28 : 32}
                  height={compact ? 28 : 32}
                  sx={{ bgcolor: 'rgba(0, 0, 0, 0.1)' }}
                />
                <Skeleton
                  variant="text"
                  width={60}
                  sx={{ bgcolor: 'rgba(0, 0, 0, 0.1)', fontSize: compact ? '14px' : '18px' }}
                />
                <Skeleton
                  variant="text"
                  width={100}
                  sx={{
                    marginLeft: 'auto',
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                    fontSize: compact ? '14px' : '18px',
                  }}
                />
              </Box>
              {index < arr.length - 1 && <Divider sx={{ marginX: '-1em' }} />}
            </Fragment>
          ))}
          {!compact && (
            <Skeleton
              variant="rectangular"
              height={48}
              sx={{
                marginTop: '1em',
                borderRadius: '8px',
                bgcolor: 'rgba(0, 0, 0, 0.1)',
              }}
            />
          )}
        </Box>
      ) : (
        <Box
          sx={
            compact
              ? { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }
              : {}
          }
        >
          {displayedAccounts.map(({ name, balance }, index, arr) => (
            <Fragment key={name}>
              <Box
                onClick={() => navigate(path(BANKING_ACCOUNT_DETAIL, { currency: name }))}
                sx={{
                  paddingY: compact ? 0 : rowPaddingY,
                  flex: compact ? 1 : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                }}
              >
                <Box
                  sx={{
                    height: compact ? '28px' : '32px',
                    width: compact ? '28px' : '32px',
                    borderRadius: '50%',
                    background: COLORS.gray[500],
                    color: COLORS.gray[1100],
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 600,
                    fontSize: compact ? '13px' : undefined,
                  }}
                >
                  {currencySymbols[name as keyof typeof currencySymbols] || '$'}
                </Box>
                <Typography
                  sx={{ color: COLORS.gray[1000], fontWeight: 600, fontSize: compact ? '14px' : '18px' }}
                >
                  {name}
                </Typography>
                <NumericText
                  sx={{
                    marginLeft: 'auto',
                    fontSize: compact ? '14px' : '20px',
                    color: COLORS.gray[1200],
                    fontWeight: 500,
                    lineHeight: compact ? '20px' : '30px',
                  }}
                >
                  {balance}
                </NumericText>
              </Box>
              {index < arr.length - 1 && <Divider sx={{ marginX: '-1em' }} />}
            </Fragment>
          ))}
          {showInlineViewAll && (
            <Fragment>
              <Divider sx={{ marginX: '-1em' }} />
              <Box
                onClick={() => navigate(BANKING_GLOBAL_ACCOUNTS)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  color: COLORS.gray[1000],
                  '&:hover': { color: COLORS.gray[1400] },
                }}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'inherit' }}>
                  {t('banking-page:globalAccounts.viewAllAccounts')}
                </Typography>
                <ArrowForward sx={{ fontSize: '16px' }} />
              </Box>
            </Fragment>
          )}
          {!compact && (
            <Button
              type="outline"
              fullWidth
              color="secondary"
              endIcon={<ArrowForward />}
              onClick={() => navigate(BANKING_GLOBAL_ACCOUNTS)}
            >
              {t('banking-page:globalAccounts.viewAllAccounts')}
            </Button>
          )}
        </Box>
      )}
    </Section>
  );
}
